import { Stack, xcss } from '@atlaskit/primitives';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { type DatabaseFieldType, USER_VALUE_CURRENT } from '@databases/types/database';
import { UserCard } from '@databases/user';
import { isNonEmptyUserValue } from '@databases/utils/database/field-types/user';
import { selectCurrentUserId } from '@databases/utils/redux/context/selectors';
import React, {
  lazy,
  Suspense,
  type FunctionComponent,
  useCallback,
  useMemo
} from 'react';
import { useIntl } from 'react-intl-next';
import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { dynamicImport } from '@canvas/dynamic-import';

import { type ViewComponentProps } from './types.ts';

const LegacyUserProfileCard = lazy(() =>
  dynamicImport(() => import('./legacy-user-profile-card.tsx'))
);
const MigratedUserProfileCard = lazy(() =>
  dynamicImport(() => import('./migrated-user-profile-card.tsx'))
);

const userContainerStyles = xcss({
  listStyle: 'none',
  padding: 'space.0',
  width: '100%',
  gap: 'space.025'
});

const UserValueView: FunctionComponent<ViewComponentProps<DatabaseFieldType.USER>> = ({
  value,
  showDisabledValue
}) => {
  const { formatMessage } = useIntl();
  const currentUserId = defaultStoreUtils.useSelector(selectCurrentUserId);

  const resolvedValue = useMemo(() => {
    if (Array.isArray(value)) {
      return value.map((user) => {
        if (
          'accountId' in user &&
          user.accountId === USER_VALUE_CURRENT &&
          currentUserId
        ) {
          return { _id: user._id, accountId: currentUserId };
        } else {
          return user;
        }
      });
    } else {
      return [];
    }
  }, [currentUserId, value]);

  const createProfileAction = useCallback(
    (accountId: string) => ({
      label: formatMessage(messages['user-value-value.profile-card-view-profile-label']),
      callback: () =>
        globalThis.open(
          `/wiki/people/${accountId}?ref=confluence`,
          '_blank',
          'noopener,noreferrer'
        )
    }),
    [formatMessage]
  );

  const avatarWrapper = useCallback(
    (accountId: string) => {
      const WrappedAvatar = (avatar: React.ReactNode) => {
        if (isFeatureFlagEnabled('people-teams_migrate-user-profile-card')) {
          return (
            <Suspense fallback={avatar}>
              <MigratedUserProfileCard
                accountId={accountId}
                currentUserId={currentUserId}
              >
                {avatar}
              </MigratedUserProfileCard>
            </Suspense>
          );
        }

        return (
          <Suspense fallback={avatar}>
            <LegacyUserProfileCard
              accountId={accountId}
              currentUserId={currentUserId}
              profileAction={createProfileAction(accountId)}
            >
              {avatar}
            </LegacyUserProfileCard>
          </Suspense>
        );
      };

      return WrappedAvatar;
    },
    [currentUserId, createProfileAction]
  );

  return (
    <Stack as="ul" xcss={userContainerStyles}>
      {resolvedValue.filter(isNonEmptyUserValue).map((user) => (
        <li key={user._id}>
          <UserCard
            accountId={user.accountId}
            currentUserId={currentUserId}
            displayName={user.displayName}
            variant="avatar"
            size="small"
            showDisabledValue={showDisabledValue}
            avatarWrapper={avatarWrapper(user.accountId)}
          />
        </li>
      ))}
    </Stack>
  );
};

export default UserValueView;
